// src/components/PeoplePicker.js
import React, { useState, useRef, useEffect, useCallback } from "react";
// PeoplePicker uses fetch directly so it can pass ownerOnly param

export default function PeoplePicker({ value, onChange, placeholder = "Search for a person...", ownerOnly = false, clearOnSelect = false }) {
  const [query, setQuery]     = useState(value?.displayName || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const [error, setError]     = useState("");
  const debounceRef           = useRef(null);
  const wrapperRef            = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setOpen(false); setError(""); return; }
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ q });
      if (ownerOnly) params.set('ownerOnly', 'true');
      const res  = await fetch(`/api/graph/users?${params}`);
      const data = await res.json();
      // API may return an error object instead of an array
      if (data && data.error) {
        setError(`Search error: ${data.error}`);
        setResults([]);
        setOpen(true);
      } else {
        setResults(data || []);
        setOpen(true);
      }
    } catch (err) {
      setError(`Search failed: ${err.message}`);
      setResults([]);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    if (value) onChange(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 300);
  };

  const handleSelect = (user) => {
    onChange(user);
    setQuery(user.displayName);
    setOpen(false);
    setResults([]);
    setError("");
  };

  const handleClear = () => {
    setQuery("");
    onChange(null);
    setResults([]);
    setOpen(false);
    setError("");
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 6 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            value={query}
            onChange={handleInput}
            placeholder={placeholder}
            autoComplete="off"
          />
          {loading && (
            <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
              <span className="spinner" style={{ width: 14, height: 14 }} />
            </span>
          )}
        </div>
        {(query || value) && (
          <button type="button" className="btn-ghost" onClick={handleClear} title="Clear"
            style={{ padding: "6px 8px", flexShrink: 0 }}>✕</button>
        )}
      </div>

      {value && (
        <div style={{ marginTop: 6, padding: "7px 10px", background: "var(--blue-light)", borderRadius: "var(--radius)", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--blue)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
            {value.displayName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>
              {value.displayName}
              {value.isCustom && <span style={{ marginLeft: 6, fontSize: 11, background: "var(--gray-200)", color: "var(--gray-500)", borderRadius: 99, padding: "1px 7px" }}>External</span>}
            </div>
            <div style={{ fontSize: 12, color: "var(--gray-500)" }}>{value.jobTitle ? `${value.jobTitle} · ` : ""}{value.email}</div>
          </div>
        </div>
      )}

      {open && !loading && error && (
        <div className="dropdown-list">
          <div className="dropdown-item" style={{ color: "var(--red)", cursor: "default", fontSize: 12 }}>
            {error}
          </div>
        </div>
      )}

      {open && !loading && !error && results.length > 0 && (
        <div className="dropdown-list">
          {results.map(user => (
            <div key={user.id} className="dropdown-item" onMouseDown={() => handleSelect(user)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--blue)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                  {user.displayName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="name">
                    {user.displayName}
                    {user.isCustom && <span style={{ marginLeft: 6, fontSize: 11, background: "var(--gray-200)", color: "var(--gray-500)", borderRadius: 99, padding: "1px 7px", fontWeight: 500 }}>External</span>}
                  </div>
                  <div className="sub">{user.jobTitle ? `${user.jobTitle} · ` : ""}{user.email}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && !loading && !error && results.length === 0 && query.length >= 2 && (
        <div className="dropdown-list">
          <div className="dropdown-item" style={{ color: "var(--gray-500)", cursor: "default" }}>
            No users found
          </div>
        </div>
      )}
    </div>
  );
}
